import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiCheck, FiChevronDown, FiLock, FiMapPin, FiUpload, FiUser } from "react-icons/fi";
import axios from "axios";
import countryFlags from "country-flag-emoji-json";
import { jwtDecode } from "jwt-decode";
import logo from "../../assets/Care_Concierge_Logo.png";
import { env } from "../../config/env";
import "../../styles/carepro-update.css";
import "../../styles/carepro-address.css";
import "../../styles/carepro-profile-photo.css";
import "../../styles/carepro-identity.css";

const emptyProfile = {
  first_name: "", last_name: "", preferred_name: "", ic_no: "", passport: "", dob: "", sex: "", nationality: "",
  marital_status: "", religion: "", height_cm: "", weight_kg: "",
  mobile: "", email: "", address: "", address2: "", city: "", state: "", postcode: "", country: "Malaysia",
  add_latitude: null, add_longitude: null,
  transportation: "",
  image_url_s3: "", fin_checked: 0,
};

const previewProfile = {
  first_name: "Aina", last_name: "Rahman", preferred_name: "Aina", ic_no: "940618-10-5678", passport: "", dob: "1994-06-18",
  sex: "female", nationality: "Malaysian", mobile: "+60 12-345 6789", email: "aina.rahman@example.com",
  address: "18, Jalan Damai", city: "Petaling Jaya", state: "Selangor", postcode: "47301",
  add_latitude: 3.1073, add_longitude: 101.6067,
  country: "Malaysia", transportation: "own_car", id: 220, image_url_s3: "",
  expires_at: "2026-08-17T23:59:59+08:00", fin_checked: 0,
};

const labelMap = {
  first_name: "First name", last_name: "Last name", preferred_name: "Preferred name",
  dob: "Date of birth", sex: "Gender", nationality: "Nationality", marital_status: "Marital status",
  religion: "Religion", height_cm: "Height", weight_kg: "Weight", mobile: "Mobile number",
};
const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;

const normalizeDateForInput = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const dayFirst = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dayFirst) return `${dayFirst[3]}-${dayFirst[2].padStart(2, "0")}-${dayFirst[1].padStart(2, "0")}`;
  return "";
};

const normalizeGender = (value) => {
  const gender = String(value ?? "").trim().toLowerCase();
  if (["m", "male"].includes(gender)) return "male";
  if (["f", "female"].includes(gender)) return "female";
  if (["o", "other"].includes(gender)) return "other";
  return gender;
};

const isMalaysian = (nationality) =>
  ["malaysian", "malaysia"].includes(String(nationality ?? "").trim().toLowerCase());

const dobFromMalaysianIc = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 12) return "";
  const yearPart = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const day = Number(digits.slice(4, 6));
  const currentYear = new Date().getFullYear();
  const year = yearPart <= currentYear % 100 ? 2000 + yearPart : 1900 + yearPart;
  const candidate = new Date(year, month - 1, day);
  if (candidate.getFullYear() !== year || candidate.getMonth() !== month - 1 || candidate.getDate() !== day || candidate > new Date()) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const calculateAge = (dob) => {
  const birthYear = Number(String(dob ?? "").slice(0, 4));
  const currentYear = new Date().getFullYear();
  if (!Number.isInteger(birthYear) || birthYear < 1900 || birthYear > currentYear) return "";
  // Match Care Manager Portal: age is the current calendar year minus DOB year.
  return String(currentYear - birthYear);
};

const toFormProfile = (details) => ({
  ...emptyProfile,
  ...details,
  preferred_name: details.nickname ?? "",
  marital_status: details.marital_status ?? "",
  religion: details.religion ?? "",
  height_cm: details.height_cm ?? "",
  weight_kg: details.weight_kg ?? "",
  transportation: details.transportation ?? "",
  dob: normalizeDateForInput(details.dob),
  sex: normalizeGender(details.sex),
  address: details.add_street ?? "",
  address2: details.add_street2 ?? "",
  city: details.add_city ?? "",
  state: details.add_state ?? "",
  postcode: details.add_postcode ?? "",
  country: details.add_country ?? "Malaysia",
});

const toExternalUpdatePayload = (profile) => ({
  first_name: profile.first_name,
  last_name: profile.last_name,
  nickname: profile.preferred_name,
  ic_no: profile.ic_no,
  passport: profile.passport,
  dob: profile.dob,
  nationality: profile.nationality,
  sex: profile.sex,
  marital_status: profile.marital_status,
  religion: profile.religion,
  height_cm: profile.height_cm,
  weight_kg: profile.weight_kg,
  mobile: profile.mobile,
  add_street: profile.address,
  add_street2: profile.address2,
  add_city: profile.city,
  add_state: profile.state,
  add_postcode: profile.postcode,
  add_country: profile.country,
  transportation: profile.transportation,
});

const changedFields = (profile, initial) => {
  const current = toExternalUpdatePayload(profile);
  const original = toExternalUpdatePayload(initial);
  return Object.fromEntries(Object.entries(current).filter(([key, value]) => value !== original[key]));
};

const getTokenExpiry = (token) => {
  if (!token || token === "preview") return null;
  try {
    const { exp } = jwtDecode(token);
    return Number.isFinite(Number(exp)) ? new Date(Number(exp) * 1000) : null;
  } catch {
    return null;
  }
};

const imageHeaders = () => env.IMAGE_ENDPOINT_KEY ? { "x-api-key": env.IMAGE_ENDPOINT_KEY } : {};
const careAdminPublicReadHeaders = () =>
  env.API_ENDPOINT_KEY ? { "x-api-key": env.API_ENDPOINT_KEY } : {};
const careAdminHeaders = (profileUpdateToken) => ({
  ...(env.API_ENDPOINT_KEY ? { "x-api-key": env.API_ENDPOINT_KEY } : {}),
  Authorization: `Bearer ${profileUpdateToken}`,
});

async function getProfileImageUrl(s3Url) {
  if (!s3Url) return "";
  const { data } = await axios.get(`${env.IMAGE_ENDPOINT_URL}/agents-images`, {
    params: { url: s3Url },
    headers: imageHeaders(),
  });
  return data?.downloadUrl || "";
}

const pinnedNationalityCodes = ["MY", "SG", "CN", "PH"];
const nationalityOptions = [
  ...pinnedNationalityCodes
    .map((code) => countryFlags.find((country) => country.code === code))
    .filter(Boolean),
  ...countryFlags.filter((country) => !pinnedNationalityCodes.includes(country.code)),
];

const malaysiaStates = ["Johor", "Kedah", "Kelantan", "Kuala Lumpur", "Labuan", "Melaka", "Negeri Sembilan", "Pahang", "Penang", "Perak", "Perlis", "Putrajaya", "Sabah", "Sarawak", "Selangor", "Terengganu"];

let googleMapsPromise;
function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (googleMapsPromise) return googleMapsPromise;
  if (!env.GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("missing-key"));

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("carepro-google-maps-script");
    const handleLoad = () => window.google?.maps?.places ? resolve(window.google.maps) : reject(new Error("load-error"));
    if (existing) { existing.addEventListener("load", handleLoad, { once: true }); return; }
    const script = document.createElement("script");
    script.id = "carepro-google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(env.GOOGLE_MAPS_API_KEY)}&libraries=places`;
    script.async = true; script.defer = true; script.addEventListener("load", handleLoad, { once: true }); script.addEventListener("error", () => reject(new Error("load-error")), { once: true });
    document.head.appendChild(script);
  });
  return googleMapsPromise;
}

function addressPart(components, type) {
  return components.find((component) => component.types?.includes(type))?.long_name || "";
}

function GoogleAddressField({ value, onChange, onPlaceSelected, coordinates }) {
  const inputRef = useRef(null);
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const [mapsStatus, setMapsStatus] = useState("idle");
  const [pendingPlace, setPendingPlace] = useState(null);

  const parsePlace = useCallback((place) => {
    const components = place.address_components || [];
    const street = [addressPart(components, "street_number"), addressPart(components, "route")].filter(Boolean).join(" ");
    const location = place.geometry?.location;
    return {
      address: street || place.formatted_address || value,
      city: addressPart(components, "locality") || addressPart(components, "postal_town") || addressPart(components, "administrative_area_level_2"),
      state: addressPart(components, "administrative_area_level_1"),
      postcode: addressPart(components, "postal_code"),
      country: addressPart(components, "country"),
      add_latitude: typeof location?.lat === "function" ? location.lat() : null,
      add_longitude: typeof location?.lng === "function" ? location.lng() : null,
    };
  }, [value]);

  const showPoint = useCallback((maps, point) => {
    if (!mapRef.current || !point) return;
    const position = { lat: Number(point.add_latitude), lng: Number(point.add_longitude) };
    mapRef.current.setCenter(position); mapRef.current.setZoom(17);
    if (markerRef.current) markerRef.current.setPosition(position);
    else markerRef.current = new maps.Marker({ map: mapRef.current, position });
  }, []);

  const enableSuggestions = async () => {
    if (autocompleteRef.current || mapsStatus === "loading") return;
    setMapsStatus("loading");
    try {
      const maps = await loadGoogleMaps();
      if (!inputRef.current) return;
      if (!mapRef.current && mapElementRef.current) {
        const hasCoordinates = Number.isFinite(Number(coordinates?.lat)) && Number.isFinite(Number(coordinates?.lng));
        const center = hasCoordinates ? { lat: Number(coordinates.lat), lng: Number(coordinates.lng) } : { lat: 3.139, lng: 101.6869 };
        mapRef.current = new maps.Map(mapElementRef.current, { center, zoom: hasCoordinates ? 17 : 11, streetViewControl: false, mapTypeControl: false, fullscreenControl: false });
        if (hasCoordinates) showPoint(maps, { add_latitude: center.lat, add_longitude: center.lng });
        mapRef.current.addListener("click", (event) => {
          const geocoder = new maps.Geocoder();
          geocoder.geocode({ location: event.latLng }, (results, resultStatus) => {
            if (resultStatus !== "OK" || !results?.[0]) return;
            const parsed = parsePlace(results[0]);
            setPendingPlace(parsed); showPoint(maps, parsed);
          });
        });
      }
      const autocomplete = new maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
        componentRestrictions: { country: "my" },
        fields: ["address_components", "formatted_address", "geometry"],
      });
      autocompleteRef.current = autocomplete;
      listenerRef.current = autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const parsed = parsePlace(place);
        setPendingPlace(null); onPlaceSelected(parsed); showPoint(maps, parsed);
      });
      setMapsStatus("ready");
    } catch { setMapsStatus("unavailable"); }
  };

  useEffect(() => () => listenerRef.current?.remove?.(), []);

  return <div className="cpu-address-editor cpu-span-2"><div className="cpu-address-heading"><span><FiMapPin/></span><div><h3>Home address</h3><p>Primary residential address</p></div></div><Field label="Address 1" name="address"><div className="cpu-address-input"><FiMapPin aria-hidden="true"/><input ref={inputRef} id="address" name="address" value={value} onChange={onChange} onFocus={enableSuggestions} autoComplete="off" placeholder="Search for address or place…"/><span aria-live="polite">{mapsStatus === "loading" ? "Loading Google Maps…" : mapsStatus === "ready" ? "Search an address or click the map to choose a location" : mapsStatus === "unavailable" ? "Google Maps unavailable — enter address manually" : "Focus this field to search with Google Maps"}</span></div></Field><div ref={mapElementRef} className={`cpu-address-map ${mapsStatus !== "ready" ? "is-waiting" : ""}`} aria-label="Address map">{mapsStatus === "idle" && <div><FiMapPin/><strong>Map preview</strong><span>Focus Address 1 to load the map</span></div>}</div>{pendingPlace && <div className="cpu-map-confirm"><span><FiMapPin/>Use the selected map location?</span><button type="button" onClick={() => { onPlaceSelected(pendingPlace); setPendingPlace(null); }}>Choose here</button></div>}</div>;
}

function Field({ label, name, required, error, hint, className = "", children, ...props }) {
  const errorId = `${name}-error`;
  return (
    <div className={`cpu-field ${className}`}>
      <label htmlFor={name}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      {children || <input id={name} name={name} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} {...props} />}
      {hint && <p className="cpu-field-hint">{hint}</p>}
      {error && <p className="cpu-field-error" id={errorId}><FiAlertCircle />{error}</p>}
    </div>
  );
}

function SelectField({ label, name, value, onChange, children, required, error }) {
  return <Field label={label} name={name} required={required} error={error}><div className="cpu-select"><select id={name} name={name} value={value} onChange={onChange} aria-invalid={Boolean(error)}>{children}</select><FiChevronDown /></div></Field>;
}

function StatusPage({ type }) {
  const expired = type === "expired";
  const unavailable = type === "unavailable";
  return <main className="cpu-state"><div className={`cpu-state-icon ${expired ? "expired" : ""}`}><FiLock /></div><p className="cpu-eyebrow">SECURE PROFILE UPDATE</p><h1>{expired ? "This profile update link has expired" : unavailable ? "We couldn't load this profile" : "We couldn't open this profile"}</h1><p>{expired ? "For your security, profile update links are available for 7 days. Please ask your Care Manager for a new link." : unavailable ? "Please try again in a moment. If the problem continues, contact Care Concierge." : "The link may be incomplete or no longer valid. Please check the full link or contact your Care Manager."}</p><a href="mailto:care@careconcierge.com.my">Contact Care Concierge</a></main>;
}

function LoadingPage() {
  return <main className="cpu-main" aria-busy="true" aria-label="Loading profile"><div className="cpu-skeleton cpu-skeleton-hero"/><div className="cpu-skeleton cpu-skeleton-card"/><div className="cpu-skeleton cpu-skeleton-card"/></main>;
}

export default function CareProUpdatePage() {
  const [status, setStatus] = useState("loading");
  const [profile, setProfile] = useState(emptyProfile);
  const [initial, setInitial] = useState(emptyProfile);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const summaryRef = useRef(null);
  const photoInputRef = useRef(null);
  const profileUpdateToken = useMemo(
    () => new URLSearchParams(window.location.search).get("profileUpdateToken"),
    [],
  );
  const tokenExpiry = useMemo(
    () => getTokenExpiry(profileUpdateToken),
    [profileUpdateToken],
  );
  const dirty = JSON.stringify(profile) !== JSON.stringify(initial);
  const profileLocked = Number(profile.fin_checked) === 1;

  useEffect(() => {
    const load = async () => {
      if (!profileUpdateToken) return setStatus("invalid");
      if (import.meta.env.DEV && profileUpdateToken === "preview") {
        await new Promise((resolve) => setTimeout(resolve, 650));
        setProfile(previewProfile); setInitial(previewProfile); setStatus("ready"); return;
      }
      try {
        const { data } = await axios.get(`${env.API_ENDPOINT_URL}/careadmin/caregivers/external`, {
          params: { profileUpdateToken },
          headers: careAdminPublicReadHeaders(),
        });
        if (!data?.success || !data?.details) return setStatus("invalid");
        const nextProfile = toFormProfile(data.details);
        setProfile(nextProfile); setInitial(nextProfile);
        if (nextProfile.image_url_s3) {
          try { setProfileImageUrl(await getProfileImageUrl(nextProfile.image_url_s3)); } catch { setProfileImageUrl(""); }
        }
        setStatus("ready");
      } catch (error) {
        setStatus(error?.response?.status === 401 ? "invalid" : "unavailable");
      }
    };
    load();
  }, [profileUpdateToken]);

  useEffect(() => {
    const warn = (event) => { if (dirty && status === "ready") { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn); return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, status]);

  const update = ({ target }) => {
    if (profileLocked) return;
    setProfile((current) => ({ ...current, [target.name]: target.value }));
  };
  const updateIdentity = ({ target }) => setProfile((current) => {
    if (profileLocked) return current;
    const next = { ...current, [target.name]: target.value };
    const nationality = target.name === "nationality" ? target.value : current.nationality;
    const icNumber = target.name === "ic_no" ? target.value : current.ic_no;
    if (isMalaysian(nationality)) {
      const derivedDob = dobFromMalaysianIc(icNumber);
      if (derivedDob) next.dob = derivedDob;
    }
    return next;
  });
  const updateAddressFromGoogle = useCallback((address) => {
    if (profileLocked) return;
    setProfile((current) => ({ ...current, ...address }));
  }, [profileLocked]);
  const validate = () => {
    const next = {};
    ["first_name", "last_name", "preferred_name", "dob", "sex", "nationality", "marital_status", "religion", "height_cm", "weight_kg", "mobile"].forEach((key) => {
      if (profile[key] == null || String(profile[key]).trim() === "") next[key] = `${labelMap[key]} is required`;
    });
    if (!profile.ic_no?.trim() && !profile.passport?.trim()) {
      next.ic_no = "Enter an IC number or passport number";
      next.passport = "Enter an IC number or passport number";
    }
    setErrors(next); if (Object.keys(next).length) setTimeout(() => summaryRef.current?.focus(), 0);
    return !Object.keys(next).length;
  };
  const save = async (event) => {
    event.preventDefault(); setSaveError(false); if (profileLocked || !validate()) return;
    setStatus("saving");
    try {
      if (!(import.meta.env.DEV && profileUpdateToken === "preview")) {
        const payload = changedFields(profile, initial);
        if (Object.keys(payload).length) {
          const { data } = await axios.put(`${env.API_ENDPOINT_URL}/careadmin/caregivers/${profile.id}`, { payload }, {
            headers: careAdminHeaders(profileUpdateToken),
          });
          if (data?.success === false) throw new Error("Profile update was rejected.");
        }
      }
      else await new Promise((resolve) => setTimeout(resolve, 800));
      setSavedAt(new Intl.DateTimeFormat("en-MY", { dateStyle: "long", timeStyle: "short", timeZone: "Asia/Kuala_Lumpur" }).format(new Date())); setInitial(profile); setStatus("success");
    } catch { setStatus("ready"); setSaveError(true); setTimeout(() => summaryRef.current?.focus(), 0); }
  };
  const cancel = () => { if (!dirty || window.confirm("Discard all changes made to your profile?")) { setProfile(initial); setErrors({}); } };
  const uploadProfilePhoto = async ({ target }) => {
    const file = target.files?.[0];
    target.value = "";
    if (!file) return;
    if (profileLocked) return setPhotoError("Profile updates are unavailable after finance approval.");
    if (!file.type.startsWith("image/")) return setPhotoError("Choose a JPG or PNG image.");
    if (file.size > MAX_PROFILE_IMAGE_SIZE) return setPhotoError("The image must be 5 MB or smaller.");
    if (!profile.id) return setPhotoError("We couldn't identify this Care Pro. Please reopen the link and try again.");

    setPhotoError(""); setIsPhotoUploading(true);
    try {
      const filename = `${Date.now()}_${file.name}`;
      const prefix = `${profile.id}/profile`;
      const { data: uploadInfo } = await axios.post(`${env.IMAGE_ENDPOINT_URL}/agents-images`, { prefix, filename }, { headers: imageHeaders() });
      if (!uploadInfo?.uploadUrl || !uploadInfo?.downloadUrl) throw new Error("Upload URL was not returned.");
      await axios.put(uploadInfo.uploadUrl, file, { headers: { "Content-Type": file.type } });
      await axios.post(`${env.API_ENDPOINT_URL}/careadmin/caregivers/images`, {
        agent_id: profile.id,
        image_filename: file.name,
        image_url_s3: uploadInfo.downloadUrl,
        taken_at: new Date().toISOString().slice(0, 19).replace("T", " "),
        type: "profile",
      }, { headers: careAdminHeaders(profileUpdateToken) });
      setProfile((current) => ({ ...current, image_url_s3: uploadInfo.downloadUrl }));
      setInitial((current) => ({ ...current, image_url_s3: uploadInfo.downloadUrl }));
      setProfileImageUrl(await getProfileImageUrl(uploadInfo.downloadUrl));
    } catch (error) {
      const message = error?.response?.data?.message;
      setPhotoError(message || "We couldn't upload your photo. Please try again.");
    } finally {
      setIsPhotoUploading(false);
    }
  };

  if (status === "loading") return <PageShell><LoadingPage /></PageShell>;
  if (status === "invalid" || status === "expired" || status === "unavailable") return <PageShell><StatusPage type={status} /></PageShell>;
  if (status === "success") return <PageShell><main className="cpu-state cpu-success"><div className="cpu-state-icon"><FiCheck /></div><p className="cpu-eyebrow">ALL DONE</p><h1>Profile updated</h1><p>Thank you. Your changes have been sent to Care Concierge.</p><div className="cpu-saved-time"><span>Saved</span><strong>{savedAt}</strong><small>Malaysia time</small></div><div className="cpu-success-actions"><button className="cpu-secondary" onClick={() => setStatus("ready")}>Return to edit profile</button><button className="cpu-primary" onClick={() => window.close()}>Close this page</button></div></main></PageShell>;

  const expiryDate = profile.expires_at ? new Date(profile.expires_at) : tokenExpiry;
  const expiry = expiryDate && !Number.isNaN(expiryDate.getTime())
    ? new Intl.DateTimeFormat("en-MY", { dateStyle: "long", timeStyle: "short", timeZone: "Asia/Kuala_Lumpur" }).format(expiryDate)
    : "—";
  const malaysianProfile = isMalaysian(profile.nationality);
  const dobDerivedFromIc = malaysianProfile && Boolean(dobFromMalaysianIc(profile.ic_no));
  const age = calculateAge(profile.dob);
  return <PageShell><main className="cpu-main" style={profileImageUrl ? { "--cpu-profile-avatar-image": `url("${profileImageUrl}")`, "--cpu-profile-avatar-color": "transparent" } : undefined}>
    <section className="cpu-hero"><div><p className="cpu-eyebrow">CARE PRO PROFILE</p><h1>Hi {profile.first_name}, keep your profile up to date</h1><p>Help us match you with the right care opportunities by checking your important details below.</p></div><div className="cpu-expiry"><FiLock/><span>This secure link expires</span><strong>{expiry}</strong></div></section>
    {(Object.keys(errors).length > 0 || saveError) && <div className="cpu-error-summary" ref={summaryRef} tabIndex="-1" role="alert"><FiAlertCircle/><div><strong>{saveError ? "We couldn't save your changes" : "Please check the highlighted fields"}</strong><p>{saveError ? "Check your connection and try again." : `${Object.keys(errors).length} field${Object.keys(errors).length > 1 ? "s need" : " needs"} your attention.`}</p></div></div>}
    {profileLocked && <div className="cpu-locked-notice" role="status"><FiLock/><div><strong>Profile updates are unavailable</strong><p>This profile has been finance-approved and can no longer be changed.</p></div></div>}
    <form onSubmit={save} noValidate>
      <fieldset className={`cpu-edit-fields ${profileLocked ? "is-locked" : ""}`} disabled={profileLocked}>
      <input ref={photoInputRef} className="cpu-photo-input" type="file" accept="image/jpeg,image/png" onChange={uploadProfilePhoto}/>
      {isPhotoUploading && <p className="cpu-photo-status" aria-live="polite">Uploading profile photo…</p>}
      {photoError && <p className="cpu-photo-error" role="alert"><FiAlertCircle/>{photoError}</p>}
      <section className="cpu-card">
        <div className="cpu-section-title"><span><FiUser/></span><div><h2>Personal details</h2><p>Tell us how you would like to be known.</p></div></div>
        <div className="cpu-photo"><div className="cpu-avatar">{profile.first_name?.[0]}{profile.last_name?.[0]}</div><div><strong>Profile photo</strong><p>JPG or PNG, up to 5 MB</p><button type="button" className="cpu-upload" disabled={isPhotoUploading} onClick={() => photoInputRef.current?.click()}><FiUpload/>{isPhotoUploading ? "Uploading…" : "Upload new photo"}</button></div></div>
        <div className="cpu-grid">
          <Field label="First name" name="first_name" required value={profile.first_name} onChange={update} error={errors.first_name}/>
          <Field label="Last name" name="last_name" required value={profile.last_name} onChange={update} error={errors.last_name}/>
          <Field label="Preferred name" name="preferred_name" required value={profile.preferred_name} onChange={update} error={errors.preferred_name}/>
          <Field label="IC number" name="ic_no" inputMode="numeric" maxLength="14" placeholder="YYMMDD-SS-####" value={profile.ic_no} onChange={updateIdentity} error={errors.ic_no}/>
          <Field label="Passport number" name="passport" value={profile.passport} onChange={update} error={errors.passport}/>
          <Field label="Date of birth" name="dob" required type="date" value={profile.dob} onChange={update} readOnly={dobDerivedFromIc} error={errors.dob}/>
          <Field label="Age" name="age" required value={age} readOnly/>
          <SelectField label="Gender" name="sex" required value={profile.sex} onChange={update} error={errors.sex}><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Prefer to self-describe</option></SelectField>
          <SelectField label="Nationality" name="nationality" required value={profile.nationality} onChange={updateIdentity} error={errors.nationality}><option value="">Select nationality</option>{profile.nationality && !nationalityOptions.some((country) => country.name === profile.nationality) && <option value={profile.nationality}>{profile.nationality}</option>}{nationalityOptions.map((country) => <option key={country.code} value={country.name}>{country.emoji} {country.name}</option>)}</SelectField>
          <SelectField label="Marital status" name="marital_status" required value={profile.marital_status} onChange={update} error={errors.marital_status}><option value="">Select marital status</option>{["single","married","divorced","widowed","others"].map((value) => <option key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</option>)}</SelectField>
          <SelectField label="Religion" name="religion" required value={profile.religion} onChange={update} error={errors.religion}><option value="">Select religion</option>{["islam","buddhism","christianity","hinduism","others"].map((value) => <option key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</option>)}</SelectField>
          <Field label="Height (cm)" name="height_cm" required type="number" min="0" max="300" value={profile.height_cm} onChange={update} error={errors.height_cm}/>
          <Field label="Weight (kg)" name="weight_kg" required type="number" min="0" max="500" step="0.1" value={profile.weight_kg} onChange={update} error={errors.weight_kg}/>
        </div>
      </section>
      <section className="cpu-card"><div className="cpu-section-title"><span>02</span><div><h2>Contact details</h2><p>How Care Concierge can reach you.</p></div></div><div className="cpu-grid"><Field label="Mobile number" name="mobile" required type="tel" value={profile.mobile} onChange={update} error={errors.mobile}/><Field label="Email address" name="email" type="email" value={profile.email} disabled/><GoogleAddressField value={profile.address} onChange={update} onPlaceSelected={updateAddressFromGoogle} coordinates={{ lat: profile.add_latitude, lng: profile.add_longitude }}/><Field className="cpu-span-2" label="Address 2" name="address2" value={profile.address2} onChange={update}/><Field label="City" name="city" value={profile.city} onChange={update}/><SelectField label="State" name="state" value={profile.state} onChange={update}><option value="">Select state</option>{profile.state && !malaysiaStates.includes(profile.state) && <option value={profile.state}>{profile.state}</option>}{malaysiaStates.map(x=><option key={x}>{x}</option>)}</SelectField><Field label="Postcode" name="postcode" inputMode="numeric" value={profile.postcode} onChange={update}/><Field label="Country" name="country" value={profile.country} onChange={update}/>{profile.add_latitude != null && profile.add_longitude != null && <div className="cpu-coordinates cpu-span-2"><FiMapPin/><span>Saved location</span><strong>{Number(profile.add_latitude).toFixed(6)}, {Number(profile.add_longitude).toFixed(6)}</strong></div>}</div></section>
      <section className="cpu-card"><div className="cpu-section-title"><span>03</span><div><h2>Transportation</h2><p>How you can travel to care appointments.</p></div></div><div className="cpu-grid"><SelectField label="Transportation" name="transportation" value={profile.transportation} onChange={update}><option value="">Select an option</option><option value="own_car">I have my own car</option><option value="motorcycle">I have a motorcycle</option><option value="public">I use public transport</option></SelectField></div></section>
      </fieldset>
      {dirty && !profileLocked && <div className="cpu-actions"><button type="button" className="cpu-secondary" onClick={cancel} disabled={status === "saving"}>Cancel changes</button><button className="cpu-primary" disabled={status === "saving"}>{status === "saving" ? "Saving…" : "Save profile updates"}</button></div>}
    </form><p className="cpu-privacy"><FiLock/> Your information is encrypted and only used to manage your Care Concierge profile.</p>
  </main></PageShell>;
}

function PageShell({ children }) { return <div className="cpu-page"><header className="cpu-header"><img src={logo} alt="Care Concierge"/><div><FiLock/> Secure profile update</div></header>{children}<footer>© {new Date().getFullYear()} Care Concierge · Need help? <a href="mailto:care@careconcierge.com.my">Contact us</a></footer></div>; }
